/**
 * CampaignCalendar — visual calendar view of all scheduled campaign programs.
 *
 * Features:
 *  • Month grid with prev/next navigation
 *  • Colour-coded dots per campaign type on each run day
 *  • Click a day → slide-in Day Detail panel listing every program due that day
 *  • Edit program config inline (content + recipients) before the run fires
 *  • Quick actions: Pause / Resume / Run Now / Cancel — shared with list view
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconDelete,
  IconEdit,
  IconError,
  IconInstant,
  IconMessage,
  IconPatients,
  IconPause,
  IconPending,
  IconPlay,
  IconSearch,
  IconSpinner,
  IconSuccess,
  IconUndo,
  IconVoiceCall,
  IconVoiceSMS,
} from '../components/icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { scheduleAPI } from '../services/api';
import { cn } from '../utils/cn';
import { configRecipients, recipientLabel } from '../utils/recipients';
import { buildDayMap } from '../utils/schedule';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const TYPE_META = {
  smart_sms:      { label: 'Smart SMS',      color: 'bg-blue-500', ring: 'ring-blue-400',   light: 'bg-blue-50 text-blue-700 border-blue-200',     Icon: IconMessage },
  sms_direct:     { label: 'SMS Direct',     color: 'bg-green-500', ring: 'ring-green-400',  light: 'bg-green-50 text-green-700 border-green-200',  Icon: IconMessage },
  bulk_voice:     { label: 'Voice SMS',      color: 'bg-purple-500', ring: 'ring-purple-400', light: 'bg-purple-50 text-purple-700 border-purple-200', Icon: IconVoiceSMS },
  elevenlabs_ivr: { label: 'AI Voice',       color: 'bg-orange-500', ring: 'ring-orange-400', light: 'bg-orange-50 text-orange-700 border-orange-200', Icon: IconVoiceCall },
  multi:          { label: 'Multi-channel',  color: 'bg-indigo-500', ring: 'ring-indigo-400', light: 'bg-indigo-50 text-indigo-700 border-indigo-200', Icon: IconInstant },
};

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'tw', label: 'Twi' },
  { value: 'ee', label: 'Ewe' },
  { value: 'ga', label: 'Ga' },
  { value: 'dag', label: 'Dagbani' },
  { value: 'fat', label: 'Fante' },
];

// ── Date helpers ──────────────────────────────────────────────────────────────

/** Return the set of day-of-month numbers (1-based) when a program runs in a given month. */
/** Format a cadence object into a human-readable string. */
function describeCadence(cadence) {
  if (!cadence) return '—';
  const DAY_LABELS = ['','Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  switch (cadence.type) {
    case 'daily':    return `Daily at ${cadence.time}`;
    case 'weekly':   return `Weekly on ${(cadence.days||[]).map(d => DAY_LABELS[d]).join(', ')} at ${cadence.time}`;
    case 'biweekly': return `Every 2 weeks on ${(cadence.days||[]).map(d => DAY_LABELS[d]).join(', ')} at ${cadence.time}`;
    case 'monthly':  return `Monthly on the ${cadence.day}${ordinal(cadence.day)} at ${cadence.time}`;
    case 'custom':   return `Custom dates at ${cadence.time}`;
    default:         return cadence.type;
  }
}

function ordinal(n) {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
}

// ── PatientSelector (inline, same logic as in ScheduledCampaigns) ─────────────

function PatientSelector({ selected, onChange }) {
  const { user } = useAuth();
  const [patients, setPatients]  = useState([]);
  const [loading, setLoading]    = useState(true);
  const [search, setSearch]      = useState('');
  const [filterDx, setFilterDx]  = useState('All');

  React.useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get('/patients', { params: { user_id: user.id } })
      .then(({ data }) => {
        setPatients(data?.patients ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  const diagnoses = ['All', ...Array.from(new Set(patients.map(p => p.diagnosis).filter(Boolean)))];

  const filtered = patients.filter(p => {
    const q = search.toLowerCase();
    return (!q || p.name?.toLowerCase().includes(q) || p.phone?.includes(q))
      && (filterDx === 'All' || p.diagnosis === filterDx);
  });

  const selectedIds = new Set(selected.map(p => p.id));

  const toggle = p => {
    if (selectedIds.has(p.id)) onChange(selected.filter(s => s.id !== p.id));
    else onChange([...selected, { id: p.id, name: p.name, phone: p.phone }]);
  };

  const allSel = filtered.length > 0 && filtered.every(p => selectedIds.has(p.id));
  const toggleAll = () => {
    if (allSel) {
      const ids = new Set(filtered.map(p => p.id));
      onChange(selected.filter(p => !ids.has(p.id)));
    } else {
      const newOnes = filtered.filter(p => !selectedIds.has(p.id)).map(p => ({ id: p.id, name: p.name, phone: p.phone }));
      onChange([...selected, ...newOnes]);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <IconPatients className="h-3.5 w-3.5 text-primary-600" />
          <span className="text-xs font-medium text-gray-700">Patient Database</span>
        </div>
        <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full',
          selected.length > 0 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500')}>
          {selected.length} selected
        </span>
      </div>
      <div className="px-3 py-2 flex gap-2 border-b border-gray-100">
        <div className="relative flex-1">
          <IconSearch className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
          <input className="input w-full pl-7 py-1.5 text-xs" placeholder="Search name or phone…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {diagnoses.length > 1 && (
          <select className="input text-xs py-1.5" value={filterDx} onChange={e => setFilterDx(e.target.value)}>
            {diagnoses.map(d => <option key={d}>{d}</option>)}
          </select>
        )}
      </div>
      <div className="max-h-44 overflow-y-auto divide-y divide-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-400 gap-2 text-xs">
            <IconSpinner className="h-4 w-4 animate-spin" /> Loading patients…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-6 text-center text-xs text-gray-400">
            {patients.length === 0 ? 'No patients in database.' : 'No matches.'}
          </div>
        ) : (
          <>
            <div className="flex items-center px-3 py-2 bg-gray-50 hover:bg-gray-100 cursor-pointer" onClick={toggleAll}>
              <input type="checkbox" readOnly checked={allSel} className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 mr-2" />
              <span className="text-xs font-semibold text-gray-600">
                {allSel ? 'Deselect all' : `Select all ${filtered.length}`}
              </span>
            </div>
            {filtered.map(p => (
              <div key={p.id}
                className={cn('flex items-center px-3 py-2 cursor-pointer', selectedIds.has(p.id) ? 'bg-primary-50' : 'hover:bg-gray-50')}
                onClick={() => toggle(p)}>
                <input type="checkbox" readOnly checked={selectedIds.has(p.id)} className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 mr-2 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.phone}{p.diagnosis ? ` · ${p.diagnosis}` : ''}</p>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Program Edit Modal ────────────────────────────────────────────────────────

function EditProgramModal({ program, onClose, onSaved }) {
  const type = program.campaign_type;
  const cfg  = program.campaign_config || {};

  const [form, setForm]       = useState({
    message:              cfg.message || '',
    sender_id:            cfg.sender_id || 'CareArena',
    health_condition:     cfg.health_condition || '',
    health_topic:         cfg.health_topic || '',
    campaign_goals:       cfg.campaign_goals || '',
    language:             cfg.language || 'en',
    max_messages:         cfg.max_messages_per_recipient || 3,
    message_delay:        cfg.message_delay_minutes || 60,
    disease_name:         cfg.disease_name || '',
    delay_between_calls:  cfg.delay_between_calls || 2,
  });
  const [changeRecipients, setChangeRecipients] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const buildNewConfig = () => {
    const recipients = changeRecipients && selectedPatients.length > 0
      ? selectedPatients.map(p => p.phone)
      : configRecipients(cfg);

    // A multi-channel programme stores recipients per channel. Writing a
    // top-level `recipients` key on one changed nothing at all: the scheduler
    // reads channels[].recipients for multi, so the edit appeared to save and
    // the campaign kept going to the old list.
    if (Array.isArray(cfg.channels)) {
      return {
        ...cfg,
        channels: cfg.channels.map(ch => ({ ...ch, recipients })),
      };
    }

    const base = { ...cfg, recipients };

    switch (type) {
      case 'sms_direct':
        return { ...base, message: form.message, sender_id: form.sender_id, health_condition: form.health_condition };
      case 'smart_sms':
        return { ...base, health_topic: form.health_topic, campaign_goals: form.campaign_goals, language: form.language,
          max_messages_per_recipient: Number(form.max_messages), message_delay_minutes: Number(form.message_delay) };
      case 'bulk_voice':
        return { ...base, disease_name: form.disease_name, campaign_goals: form.campaign_goals, language: form.language };
      case 'elevenlabs_ivr':
        return { ...base, health_topic: form.health_topic, delay_between_calls: Number(form.delay_between_calls) };
      default:
        return base;
    }
  };

  const handleSave = async () => {
    if (type === 'sms_direct' && !form.message.trim()) { setError('Message is required'); return; }
    if ((type === 'smart_sms' || type === 'elevenlabs_ivr') && !form.health_topic.trim()) { setError('Health topic is required'); return; }
    if (type === 'bulk_voice' && !form.disease_name.trim()) { setError('Disease / condition is required'); return; }
    if (changeRecipients && selectedPatients.length === 0) { setError('Select at least one patient'); return; }

    setSaving(true);
    setError('');
    try {
      await scheduleAPI.update(program.id, { campaign_config: buildNewConfig() });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const meta = TYPE_META[type] || TYPE_META.smart_sms;
  const Icon = meta.Icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', meta.light, 'border')}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Edit Program</h2>
              <p className="text-xs text-gray-500 truncate max-w-xs">{program.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100">
            <IconClose className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* SMS Direct: message */}
          {type === 'sms_direct' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message * <span className="text-xs text-gray-400">(max 160 chars)</span>
                </label>
                <textarea className="input w-full resize-none text-sm" rows={4} maxLength={160}
                  value={form.message} onChange={e => set('message', e.target.value)} />
                <p className="text-xs text-gray-400 mt-1">{form.message.length}/160</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sender ID</label>
                  <input className="input w-full text-sm" value={form.sender_id} onChange={e => set('sender_id', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Health condition</label>
                  <input className="input w-full text-sm" placeholder="Preeclampsia" value={form.health_condition} onChange={e => set('health_condition', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* Smart SMS */}
          {type === 'smart_sms' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Health topic *</label>
                <input className="input w-full text-sm" placeholder="Preeclampsia warning signs"
                  value={form.health_topic} onChange={e => set('health_topic', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign goals</label>
                <input className="input w-full text-sm" placeholder="Educate mothers about danger signs"
                  value={form.campaign_goals} onChange={e => set('campaign_goals', e.target.value)} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                  <select className="input w-full text-sm" value={form.language} onChange={e => set('language', e.target.value)}>
                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max msgs</label>
                  <input type="number" min={1} max={5} className="input w-full text-sm"
                    value={form.max_messages} onChange={e => set('max_messages', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delay (min)</label>
                  <input type="number" min={0} className="input w-full text-sm"
                    value={form.message_delay} onChange={e => set('message_delay', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* Bulk Voice */}
          {type === 'bulk_voice' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disease / condition *</label>
                <input className="input w-full text-sm" placeholder="Preeclampsia"
                  value={form.disease_name} onChange={e => set('disease_name', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign goals</label>
                <input className="input w-full text-sm" placeholder="Monthly blood pressure reminder"
                  value={form.campaign_goals} onChange={e => set('campaign_goals', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <select className="input w-full text-sm" value={form.language} onChange={e => set('language', e.target.value)}>
                  {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
            </>
          )}

          {/* ElevenLabs IVR */}
          {type === 'elevenlabs_ivr' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Health topic *</label>
                <input className="input w-full text-sm" placeholder="Preeclampsia education"
                  value={form.health_topic} onChange={e => set('health_topic', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delay between calls (seconds)</label>
                <input type="number" min={1} className="input w-full text-sm"
                  value={form.delay_between_calls} onChange={e => set('delay_between_calls', e.target.value)} />
              </div>
            </>
          )}

          {/* Recipients section */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-gray-700">Recipients</p>
                <p className="text-xs text-gray-400">
                  Currently {recipientLabel(cfg)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setChangeRecipients(v => !v); setSelectedPatients([]); }}
                className={cn('text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors',
                  changeRecipients
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300')}
              >
                {changeRecipients
                  ? <><IconUndo className="h-3.5 w-3.5" /> Keep existing</>
                  : <><IconEdit className="h-3.5 w-3.5" /> Change recipients</>}
              </button>
            </div>
            {changeRecipients && (
              <PatientSelector selected={selectedPatients} onChange={setSelectedPatients} />
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              <IconError className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <button onClick={onClose} className="btn btn-secondary text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary text-sm flex items-center gap-2 disabled:opacity-60">
            {saving && <IconSpinner className="h-3.5 w-3.5 animate-spin" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Day Detail Panel ──────────────────────────────────────────────────────────

function DayDetail({ date, programs, onClose, onPause, onResume, onDelete, onRunNow, onEdit }) {
  const label = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="h-full flex flex-col">
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-bold text-gray-900">{label}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {programs.length} campaign{programs.length !== 1 ? 's' : ''} scheduled
          </p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100">
          <IconClose className="h-4 w-4" />
        </button>
      </div>

      {/* Program list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {programs.map(prog => {
          const meta = TYPE_META[prog.campaign_type] || TYPE_META.smart_sms;
          const Icon = meta.Icon;
          const cfg  = prog.campaign_config || {};
          const isActive = prog.status === 'active';

          return (
            <div key={prog.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              {/* Program header */}
              <div className="px-4 py-3 flex items-start gap-3">
                <div className={cn('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border', meta.light)}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{prog.name}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <IconPending className="h-3 w-3" />
                      {prog.cadence?.time || '—'}
                    </span>
                    <span className="flex items-center gap-1">
                      <IconPatients className="h-3 w-3" />
                      {recipientLabel(cfg)}
                    </span>
                    <span className={cn('px-1.5 py-0.5 rounded-full text-xs font-medium border', meta.light)}>
                      {meta.label}
                    </span>
                  </div>
                  {/* Content preview */}
                  {cfg.message && (
                    <p className="text-xs text-gray-600 mt-2 bg-gray-50 rounded p-2 line-clamp-2">{cfg.message}</p>
                  )}
                  {cfg.health_topic && (
                    <p className="text-xs text-gray-500 mt-1">Topic: <span className="font-medium text-gray-700">{cfg.health_topic}</span></p>
                  )}
                  {cfg.disease_name && (
                    <p className="text-xs text-gray-500 mt-1">Condition: <span className="font-medium text-gray-700">{cfg.disease_name}</span></p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{describeCadence(prog.cadence)}</p>
                </div>
              </div>

              {/* Actions row */}
              <div className="flex items-center gap-1 px-4 py-2 bg-gray-50 border-t border-gray-100">
                <button
                  onClick={() => onEdit(prog)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <IconEdit className="h-3 w-3" /> Edit
                </button>
                <button
                  onClick={() => onRunNow(prog.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <IconInstant className="h-3 w-3" /> Run now
                </button>
                {isActive ? (
                  <button
                    onClick={() => onPause(prog.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                  >
                    <IconPause className="h-3 w-3" /> IconPause
                  </button>
                ) : prog.status === 'paused' ? (
                  <button
                    onClick={() => onResume(prog.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <IconPlay className="h-3 w-3" /> Resume
                  </button>
                ) : null}
                <button
                  onClick={() => onDelete(prog.id)}
                  className="ml-auto p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                  title="Cancel program"
                >
                  <IconDelete className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Calendar Component ───────────────────────────────────────────────────

export default function CampaignCalendar({
  programs,
  onPause,
  onResume,
  onDelete,
  onRunNow,
  onRefresh,
  initialMonth,
}) {
  const today = new Date();
  const initDate = initialMonth ? new Date(initialMonth) : today;

  const [viewYear,  setViewYear]  = useState(initDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initDate.getMonth());
  const [selectedDay, setSelectedDay]       = useState(null); // day-of-month number
  // Default off: the calendar answers "what is still going to run".
  // Turning it on adds the occurrences that already fired and the
  // campaigns that are paused, for when someone is reconstructing what
  // happened rather than planning what will.
  const [showHistory, setShowHistory]       = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Sync view when the parent updates initialMonth (e.g. after a new program is created)
  useEffect(() => {
    if (!initialMonth) return;
    const d = new Date(initialMonth);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setSelectedDay(null);
  }, [initialMonth]);

  // Navigate months
  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
    setSelectedDay(null);
  };
  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDay(null);
  };

  // Build calendar grid
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  const daysInMonth     = new Date(viewYear, viewMonth + 1, 0).getDate();
  const dayMap          = buildDayMap(programs, viewYear, viewMonth, { includePast: showHistory, today });
  const totalCells      = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7;

  const isToday = (d) =>
    d === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

  // The day sidebar takes programmes, not occurrence entries.
  const selectedPrograms = selectedDay
    ? (dayMap[selectedDay] || []).map(e => e.program)
    : [];

  const handleEdit = (prog) => setEditingProgram(prog);

  const handleSaved = () => {
    setEditingProgram(null);
    showToast('Program updated — changes take effect on the next run');
    onRefresh();
  };

  const handleDelete = async (id) => {
    // Parent's onDelete already prompts for confirmation — no double-confirm here
    await onDelete(id);
    setSelectedDay(null);
  };

  return (
    <div className="flex gap-6 items-start">
      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2',
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white',
        )}>
          {toast.type === 'error' ? <IconError className="h-4 w-4" /> : <IconSuccess className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      {/* ── Calendar panel ── */}
      <div className={cn('bg-white rounded-2xl border border-gray-200 shadow-sm transition-all duration-300',
        selectedDay ? 'flex-1' : 'w-full')}>

        {/* Month navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <button onClick={prevMonth} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors">
              <IconChevronLeft className="h-4 w-4" />
            </button>
            <h2 className="text-base font-bold text-gray-900 min-w-[160px] text-center">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </h2>
            <button onClick={nextMonth} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors">
              <IconChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button onClick={goToday} className="text-xs font-medium text-primary-600 hover:text-primary-800 px-3 py-1.5 rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors">
            Today
          </button>
        </div>

        {/* Legend — and a plain statement of what is on screen, because the
            team could not tell whether a dot meant "will run" or "did run". */}
        <div className="flex items-center justify-between gap-4 px-6 py-2.5 border-b border-gray-50 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            {Object.entries(TYPE_META).map(([key, m]) => (
              <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className={cn('w-2.5 h-2.5 rounded-full', m.color)} />
                {m.label}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {showHistory && (
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <span className="w-2.5 h-2.5 rounded-full ring-1 ring-inset ring-gray-400" />
                already ran or paused
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowHistory(v => !v)}
              aria-pressed={showHistory}
              className={cn(
                'text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors',
                showHistory
                  ? 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50',
              )}
            >
              {showHistory ? 'Showing everything' : 'Show past runs'}
            </button>
          </div>
        </div>

        {/* Day-name header */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {DAY_NAMES.map(d => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {Array.from({ length: totalCells }, (_, i) => {
            const dayNum = i - firstDayOfMonth + 1;
            const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const progs   = inMonth ? (dayMap[dayNum] || []) : [];
            const isSelected = selectedDay === dayNum && inMonth;

            // Up to 3 type dots, then a +N overflow
            const dots     = progs.slice(0, 3);
            const overflow = progs.length - dots.length;

            return (
              <div
                key={i}
                onClick={() => inMonth && setSelectedDay(isSelected ? null : dayNum)}
                className={cn(
                  'min-h-[80px] p-2 border-b border-r border-gray-100 flex flex-col transition-colors',
                  inMonth ? 'cursor-pointer hover:bg-gray-50' : 'bg-gray-50/50 cursor-default',
                  isSelected && 'bg-primary-50 border-primary-200 ring-1 ring-inset ring-primary-300',
                  !inMonth && 'opacity-40',
                )}
              >
                {inMonth && (
                  <>
                    {/* Day number */}
                    <span className={cn(
                      'self-start text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full mb-1',
                      isToday(dayNum)
                        ? 'bg-primary-600 text-white'
                        : isSelected
                          ? 'text-primary-700'
                          : 'text-gray-700',
                    )}>
                      {dayNum}
                    </span>

                    {/* Campaign dots */}
                    <div className="flex flex-wrap gap-1 mt-auto">
                      {dots.map(({ program: prog, isPast, isDormant }, idx) => {
                        const dotMeta = TYPE_META[prog.campaign_type] || TYPE_META.smart_sms;
                        const note = isDormant ? ' — paused' : isPast ? ' — already ran' : '';
                        return (
                          <span
                            key={idx}
                            title={`${prog.name}${note}`}
                            className={cn(
                              'flex-shrink-0 w-2 h-2 rounded-full',
                              // A run that already happened reads as an outline;
                              // one still to come is solid. Filling both the same
                              // was why a daily campaign told you nothing about
                              // what was left.
                              isPast || isDormant
                                ? cn('bg-transparent ring-1 ring-inset', dotMeta.ring || 'ring-gray-300')
                                : dotMeta.color,
                            )}
                          />
                        );
                      })}
                      {overflow > 0 && (
                        <span className="text-xs text-gray-400 leading-none">+{overflow}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {/* Say which of the two reasons the grid is empty, rather than
            claiming there are no programmes when there are several that have
            simply all finished. */}
        {Object.keys(dayMap).length === 0 && (
          <div className="text-center py-10 text-sm text-gray-400">
            {programs.length === 0
              ? 'No programs yet. Create one to see it on the calendar.'
              : showHistory
                ? 'Nothing scheduled or run this month.'
                : 'Nothing left to run this month. Turn on “Show past runs” to see what already went out.'}
          </div>
        )}
      </div>

      {/* ── Day detail sidebar ── */}
      {selectedDay && (
        <div className="w-96 flex-shrink-0 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
          style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          <DayDetail
            date={new Date(viewYear, viewMonth, selectedDay)}
            programs={selectedPrograms}
            onClose={() => setSelectedDay(null)}
            onPause={async (id) => { await onPause(id); showToast('Program paused'); onRefresh(); }}
            onResume={async (id) => { await onResume(id); showToast('Program resumed'); onRefresh(); }}
            onDelete={handleDelete}
            onRunNow={async (id) => { await onRunNow(id); showToast('Run triggered'); }}
            onEdit={handleEdit}
          />
          {selectedPrograms.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-gray-400">
              No campaigns scheduled for this day.
            </div>
          )}
        </div>
      )}

      {/* ── Edit modal ── */}
      {editingProgram && (
        <EditProgramModal
          program={editingProgram}
          onClose={() => setEditingProgram(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

